import { useState, useCallback, useRef } from 'react';
import { MessageResponse, conversationsService } from '@/services/conversations';
import { AxiosError } from 'axios';
import { translateApiError } from '@/lib/apiError';
import i18n from '@/i18n/config';

interface Source {
  document_name: string;
  document_id: string;
  text: string;
}

interface StreamState {
  isStreaming: boolean;
  thinking: string;
  sources: Source[];
  currentMessage: string;
  error: string | null;
  lastResponse: MessageResponse | null;
  isQuotaExceeded: boolean;
}

interface StreamOptions {
  context?: {
    domain_id?: string;
    use_case_id?: string;
    cadrage_id?: string;
    theme_codes?: string[];
    folder_paths?: string[];
  };
  conversationId?: string;
  chatbotId?: string;
}

export const useRAGStream = () => {
  const [state, setState] = useState<StreamState>({
    isStreaming: false,
    thinking: '',
    sources: [],
    currentMessage: '',
    error: null,
    lastResponse: null,
    isQuotaExceeded: false,
  });

  const typingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingTextRef = useRef('');
  const responseRef = useRef<MessageResponse | null>(null);

  const stopTypingAnimation = useCallback(() => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
  }, []);

  const maybeFinishStreaming = useCallback(() => {
    if (!responseRef.current || pendingTextRef.current.length > 0) return;
    stopTypingAnimation();
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      lastResponse: responseRef.current,
    }));
  }, [stopTypingAnimation]);

  const ensureTypingAnimation = useCallback(() => {
    if (typingIntervalRef.current) return;

    // Fast typing simulation on frontend, even if backend sends large chunks at once.
    typingIntervalRef.current = setInterval(() => {
      const pending = pendingTextRef.current;
      if (!pending) {
        maybeFinishStreaming();
        return;
      }

      const nextSlice = pending.slice(0, 5);
      pendingTextRef.current = pending.slice(nextSlice.length);
      setState((prev) => ({
        ...prev,
        currentMessage: prev.currentMessage + nextSlice,
      }));

      if (pendingTextRef.current.length === 0) {
        maybeFinishStreaming();
      }
    }, 12);
  }, [maybeFinishStreaming]);

  const sendMessage = useCallback(async (query: string, options?: StreamOptions) => {
    stopTypingAnimation();
    pendingTextRef.current = '';
    responseRef.current = null;

    setState({
      isStreaming: true,
      thinking: i18n.t('chatPage.generatingAnswer'),
      sources: [],
      currentMessage: '',
      error: null,
      lastResponse: null,
      isQuotaExceeded: false,
    });

    try {
      if (!options?.conversationId) {
        throw new Error('conversationId is required');
      }

      const response = await conversationsService.sendMessage(options.conversationId, {
        content: query,
        context: options?.context,
      });
      responseRef.current = response;
      pendingTextRef.current = response.assistant_message.content || '';
      setState((prev) => ({
        ...prev,
        thinking: '',
        sources: response.assistant_message.metadata?.sources || [],
      }));
      ensureTypingAnimation();
    } catch (error) {
      console.error('Error in sendMessage:', error);
      stopTypingAnimation();
      pendingTextRef.current = '';
      responseRef.current = null;

      const axiosError = error as AxiosError;
      const detail = (axiosError?.response?.data as Record<string, unknown> | undefined)?.detail;
      const isQuotaExceeded =
        axiosError?.response?.status === 403 &&
        typeof detail === 'object' &&
        detail !== null &&
        (detail as Record<string, unknown>)['code'] === 'org_quota_exceeded';

      setState((prev) => ({
        ...prev,
        isStreaming: false,
        error: isQuotaExceeded
          ? 'org_quota_exceeded'
          : translateApiError(error, i18n.t('chatPage.sendMessageError')),
        isQuotaExceeded,
      }));
    }
  }, [ensureTypingAnimation, maybeFinishStreaming, stopTypingAnimation]);

  const cancel = useCallback(() => {
    stopTypingAnimation();
    pendingTextRef.current = '';
    responseRef.current = null;
    setState((prev) => ({ ...prev, isStreaming: false }));
  }, [stopTypingAnimation]);

  const reset = useCallback(() => {
    cancel();
    setState({
      isStreaming: false,
      thinking: '',
      sources: [],
      currentMessage: '',
      error: null,
      lastResponse: null,
      isQuotaExceeded: false,
    });
  }, [cancel]);

  return {
    ...state,
    sendMessage,
    cancel,
    reset,
  };
};
